// src/components/Staking/Stake.js

import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Typography,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Box
} from '@mui/material';
import { StakingContext } from '../../context/StakingContext';
import { WalletContext } from '../../context/WalletContext';
import { ethers } from 'ethers';
import { loadTokenContract } from '../../utils/loadBlockchain';
import { NFTContext } from '../../context/NFTContext'; 
import { toast } from 'react-toastify';

const formatNumber = (num) => {
  const number = parseFloat(num);
  if (isNaN(number)) return '0';
  if (number >= 1e12) return (number / 1e12).toFixed(1) + 'T';
  if (number >= 1e9) return (number / 1e9).toFixed(1) + 'B';
  if (number >= 1e6) return (number / 1e6).toFixed(1) + 'M';
  if (number >= 1e3) return (number / 1e3).toFixed(1) + 'K';
  return number.toFixed(1);
};

const formatTime = (seconds) => {
  if (seconds <= 0) return "Expired";
  const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
  const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const Stake = () => {
  const { 
    stakeTokens, 
    isInitialized, 
    stakingPeriod, 
    setStakingPeriod, 
    loadingStake,
    totalStaked, 
    minimumStakeAmount, 
    stakingContract
  } = useContext(StakingContext);
  
  const { 
    account, 
    provider,
  } = useContext(WalletContext);

  const {
    hasMintedCouncil,
  } = useContext(NFTContext);

  const [stakeAmount, setStakeAmount] = useState('');
  const [cashBalance, setCashBalance] = useState('0');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [stakingPeriodLabels] = useState(['1 Week', '3 Weeks', '6 Weeks', '49 Weeks']);

  const cashTokenContractRef = useRef(null);

  const [timeLeft, setTimeLeft] = useState(null);
  const [contractStartTime, setContractStartTime] = useState(null);

 
  useEffect(() => {
    const initialize = async () => {
      if (provider && account) {
        try {
          const tokenContract = await loadTokenContract(provider);
          if (!tokenContract) {
            throw new Error('Failed to load $CASH token contract.');
          }
          cashTokenContractRef.current = tokenContract;

          if (typeof tokenContract.balanceOf !== 'function') {
            throw new Error('Token contract missing balanceOf function');
          }

          const cashBalanceBig = await tokenContract.balanceOf(account);
          setCashBalance(ethers.utils.formatUnits(cashBalanceBig, 18));

          const filterTo = tokenContract.filters.Transfer(null, account);
          const filterFrom = tokenContract.filters.Transfer(account, null);

          const handleTransfer = async () => {
            try {
              const balanceBig = await tokenContract.balanceOf(account);
              const updatedBalance = ethers.utils.formatUnits(balanceBig, 18);
              setCashBalance(updatedBalance);
            } catch (error) {
              console.error('Error updating balance:', error);
            }
          };

          tokenContract.on(filterTo, handleTransfer);
          tokenContract.on(filterFrom, handleTransfer);

          return () => {
            if (cashTokenContractRef.current) {
              cashTokenContractRef.current.removeListener(filterTo, handleTransfer);
              cashTokenContractRef.current.removeListener(filterFrom, handleTransfer);
              cashTokenContractRef.current = null;
            }
          };
        } catch (error) {
          console.error('Initialization error:', error);
          toast.error(`Failed to initialize staking component: ${error.message}`);
        }
      }
    };

    initialize();
  }, [provider, account]);

  useEffect(() => {
    const initializeAndFetchStartTime = async () => {
      if (!isInitialized || !stakingContract) return;

      try {
        const code = await provider.getCode(stakingContract.address);
        if (code === '0x') return;

        const startTime = await stakingContract.getStakingStartTime();
        setContractStartTime(startTime.toNumber());
      } catch (error) {
        console.error('Error fetching staking contract start time:', error);
      }
    };

    initializeAndFetchStartTime();
  }, [isInitialized, stakingContract, provider]);

  // Calculate deadline based on contract start time
  const deadline = contractStartTime ? contractStartTime + (3 * 24 * 60 * 60) : null;

  // Update countdown timer
  useEffect(() => {
    if (!deadline) return;

    const updateTimeLeft = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = deadline - now;
      setTimeLeft(remaining > 0 ? remaining : 0);
    };

    updateTimeLeft();
    const timer = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  const handleStake = async () => {
    if (!isInitialized) {
      toast.error('Staking contract not initialized.');
      return;
    }

    if (!stakeAmount) {
      toast.warning('Please enter an amount to stake.');
      return;
    }

    const amountNumber = parseFloat(stakeAmount);
    if (isNaN(amountNumber) || amountNumber <= 0) {
      toast.warning('Please enter a valid stake amount.');
      return;
    }

    if (amountNumber < parseFloat(minimumStakeAmount)) {
      toast.warning(`Minimum stake amount is ${minimumStakeAmount} $CASH.`);
      return;
    }

    try {
      await stakeTokens(stakeAmount, stakingPeriod);
      setStakeAmount('');
    } catch (error) {
      if (!error.message.includes('User rejected') && 
          !error.message.includes('user rejected') && 
          !error.message.includes('user denied')) {
        console.error('Staking error:', error);
        toast.error(error.message || 'Failed to stake tokens.');
      }
    }
  };

  const handleSetHalfBalance = () => {
    const parsedBalance = parseFloat(cashBalance);
    const parsedMinimumStake = parseFloat(minimumStakeAmount);
    if (isNaN(parsedBalance) || parsedBalance === 0) {
      toast.error('Cannot set 50% stake. Invalid balance.');
      return;
    }
    const finalAmount = (parsedBalance / 2).toFixed(18);
    if (parseFloat(finalAmount) < parsedMinimumStake) {
      toast.warning(`Cannot set 50% stake. Minimum stake amount is ${parsedMinimumStake} $CASH.`);
      return;
    }
    setStakeAmount(finalAmount);
  };

  const handleSetMaxBalance = () => {
    const parsedBalance = parseFloat(cashBalance);
    const parsedMinimumStake = parseFloat(minimumStakeAmount);
    if (isNaN(parsedBalance) || parsedBalance === 0) {
      toast.error('Cannot set Max stake. Invalid balance.');
      return;
    }
    if (parsedBalance < parsedMinimumStake) {
      toast.warning(`Cannot set Max stake. Minimum stake amount is ${parsedMinimumStake} $CASH.`);
      return;
    }
    setStakeAmount(parsedBalance.toFixed(18));
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <div className="staking-section" style={styles.stakingSection}>
      <div className="stake-options" style={styles.stakeOptions}>
        <Typography 
          variant="h4" 
          gutterBottom 
          className="title-glow section-heading"
          sx={{
            fontWeight: 700,
            letterSpacing: '0.1rem',
            textAlign: 'center',
            textTransform: 'uppercase',
            mb: 3,
            fontFamily: 'var(--font-primary)',
            fontSize: '2.5rem'
          }}
        >
          $CASH Staking
        </Typography>
        
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            display: 'block', 
            textAlign: 'center', 
            opacity: 0.7,
            mt: -2,
            mb: 2
          }}
        >
          Staking is only available for Unichain $CASH
        </Typography>

        {parseFloat(cashBalance) <= 0 && (
          <Typography variant="body2" color="textSecondary">
            You currently have no $CASH tokens. Transfer $CASH to your wallet to start staking.
          </Typography>
        )}

        {/* Display $CASH balance */}
        <Typography variant="body1" gutterBottom>
          Your $CASH: <strong>{formatNumber(cashBalance)}</strong>
        </Typography>

        {/* Display Total $CASH Staked */}
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Cumulative $CASH Staked: <strong>{formatNumber(totalStaked)} </strong>
        </Typography>

        {/* Bonus Information */}
        {hasMintedCouncil && (
          <Typography variant="body2" color="success.main" gutterBottom>
            🎉 You are a Council NFT holder and receive a 10% bonus on rewards!
          </Typography>
        )}
        
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel
            id="staking-period-label"
            sx={{
              transform: 'translate(55px, 0px) scale(1)',
              transition: 'none !important',
            }}
          >
            Staking Period
          </InputLabel>
          <Select
            labelId="staking-period-label"
            value={stakingPeriod}
            onChange={(e) => setStakingPeriod(Number(e.target.value))} // Convert to number here
            label="Staking Period"
          >
            {stakingPeriodLabels.map((label, index) => (
              <MenuItem key={index} value={index}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div className="staking-amount">
          <TextField
            id="stakeAmount"
            label="Amount of $CASH"
            type="number" 
            variant="outlined"
            placeholder="0.0"
            value={stakeAmount}
            onChange={(e) => setStakeAmount(e.target.value)} 
            fullWidth
            sx={{
              '& .MuiInputLabel-root': {
                transform: 'translate(55px, 10px) scale(1)',
                transition: 'none !important',
              },
            }}
            inputProps={{
              min: '0',
              step: '0.0001',
            }}
          />
        </div>
        <Box className="stake-buttons" sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button
            variant="outlined"
            onClick={handleSetHalfBalance}
            disabled={loadingStake}
            fullWidth
          >
            50%
          </Button>
          <Button
            variant="outlined"
            onClick={handleSetMaxBalance}
            disabled={parseFloat(cashBalance) === 0 || loadingStake}
            fullWidth
          >
            Max
          </Button>
        </Box>

        <Button
          onClick={handleStake}
          variant="contained"
          color="primary"
          disabled={
            loadingStake ||
            !stakeAmount ||
            parseFloat(stakeAmount) > parseFloat(cashBalance) ||
            parseFloat(stakeAmount) < parseFloat(minimumStakeAmount) ||
            !isInitialized
          }
          fullWidth
          sx={{ mt: 2 }}
        >
          {loadingStake ? (
            <>
              <CircularProgress size={24} color="inherit" />
              {' Staking...'}
            </>
          ) : (
            'Stake'
          )}
        </Button>
        {timeLeft !== null && timeLeft > 0 && (
          <Typography 
            variant="caption" 
            color="primary" 
            sx={{ 
              fontSize: '0.7rem', 
              mt: 1, 
              textAlign: 'center', 
              display: 'block',
              fontWeight: 'bold'
            }}
          >
            ⏰ Earn OG 49er NFT: {formatTime(timeLeft)}
          </Typography>
        )}
        <Typography 
          variant="caption" 
          color="textSecondary" 
          sx={{ 
            fontSize: '0.6rem', 
            mt: 0.5, 
            textAlign: 'center', 
            display: 'block' 
          }}
        >
          * minimum stake amount: {formatNumber(minimumStakeAmount)} $CASH
        </Typography>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

const styles = {
  stakingSection: {
    padding: '20px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    maxWidth: '500px',
    margin: '20px auto',
    backgroundColor: '#f9f9f9',
  },
  stakeOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
};

export default Stake;